import { AxiosRequestConfig } from 'axios';
import instance, { ADMIN_PREFIX } from 'services/api';

/**
 * POST /aws-s3 — primary S3 upload entrypoint used by FileUpload hook,
 * CKEditor adapter, and any other consumer that needs to push a file
 * blob into our object store.
 */
export const uploadFile = (formData: FormData, options?: AxiosRequestConfig) =>
  instance.post(`${ADMIN_PREFIX}/storage`, formData, options);

/**
 * POST <path> — variant for the Dragger component which accepts an
 * override path. Defaults to /aws-s3 at the call site. The path is
 * always relative to ADMIN_PREFIX.
 */
export const uploadFileToPath = (
  path: string,
  formData: FormData,
  options?: AxiosRequestConfig
) => {
  const normalized = path.startsWith('/') ? path : `/${path}`;

  return instance.post(`${ADMIN_PREFIX}${normalized}`, formData, options);
};

/**
 * GET <url> with `responseType: 'blob'`. Backs every file-download
 * flow that needs the raw bytes (PdfViewer, FinancialReports invoice
 * preview, openFile in utils). Pre-pends ADMIN_PREFIX when the url is
 * a relative path; absolute URLs (with protocol+host) pass through.
 */
export const fetchBlob = (url: string, options?: AxiosRequestConfig) => {
  const finalUrl = /^https?:\/\//.test(url)
    ? url
    : `${ADMIN_PREFIX}${url.startsWith('/') ? url : `/${url}`}`;

  return instance.get(finalUrl, { responseType: 'blob', ...options });
};

/**
 * GET /storage/file/url/:name — resolves a per-user signed download
 * URL for an appointment file. patientId scopes the lookup to a
 * specific user's file ACL.
 */
export const fetchStorageFileUrl = (
  filename: string,
  patientId?: string | number | null
) =>
  instance.get<{ data: { url: string } }>(
    `${ADMIN_PREFIX}/storage/file/url`,
    patientId != null
      ? { params: { user_id: patientId, name: filename } }
      : undefined
  );
